import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, MoreHorizontal, Ban } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VoucherResponse } from "@/app/types/voucher";
import { format, parseISO } from "date-fns";

export type CreditNoteTableItem = VoucherResponse;

// Type for action handlers passed to columns
interface CreditNoteActionHandlers {
  onView: (creditNote: CreditNoteTableItem) => void;
  onCancel: (creditNote: CreditNoteTableItem) => void;
}

export const createColumns = ({ onView, onCancel }: CreditNoteActionHandlers): ColumnDef<CreditNoteTableItem>[] => [
  {
    accessorKey: "voucher_number",
    header: "Credit Note #",
    cell: ({ row }) => (
      <div className="font-medium">{row.original.voucher_number}</div>
    ),
  },
  {
    accessorKey: "party.name",
    header: "Customer",
    cell: ({ row }) => (
      <div className="max-w-[200px] truncate" title={row.original.party?.name}>
        {row.original.party?.name || "—"}
      </div>
    ),
  },
  {
    accessorKey: "reference_number",
    header: "Reference",
    cell: ({ row }) => (
      <div className="max-w-[200px] truncate" title={row.original.reference_number || ""}>
        {row.original.reference_number || "—"}
      </div>
    ),
  },
  {
    accessorKey: "transaction_date",
    header: "Date",
    cell: ({ row }) => {
      if (!row.original.transaction_date) return "—";
      try {
        return format(
          typeof row.original.transaction_date === "string"
            ? parseISO(row.original.transaction_date)
            : row.original.transaction_date,
          "MMM dd, yyyy"
        );
      } catch (error) {
        console.error("Date parsing error:", error);
        return "Invalid date";
      }
    },
  },
  {
    accessorKey: "total_amount",
    header: "Amount",
    cell: ({ row }) => {
      const amount = row.original.total_amount
        ? typeof row.original.total_amount === "string"
          ? parseFloat(row.original.total_amount)
          : row.original.total_amount
        : 0;

      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(amount);
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const creditNote = row.original;

      // Determine status based on multiple fields
      let status = "Draft";
      let variant: "default" | "secondary" | "destructive" | "outline" = "default";

      if (creditNote.is_cancelled) {
        status = "Cancelled";
        variant = "destructive";
      } else if (creditNote.payment_status === "Paid" || creditNote.status === "Paid") {
        status = "Refunded";
        variant = "secondary";
      } else if (creditNote.status === "Posted") {
        status = "Posted";
        variant = "outline";
      }

      return (
        <Badge variant={variant}>
          {status}
        </Badge>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const creditNote = row.original;
      const isCancelled = creditNote.is_cancelled;
      const isRefunded = (creditNote as any).payment_status === "Paid" || (creditNote as any).status === "Paid";
      const isDraft = (creditNote as any).status === "Draft" || !(creditNote as any).status;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onView(creditNote)}
              className="cursor-pointer"
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>

            {!isCancelled && !isRefunded && isDraft && (
              <DropdownMenuItem
                onClick={() => onCancel(creditNote)}
                className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Ban className="mr-2 h-4 w-4" />
                Cancel Credit Note
              </DropdownMenuItem>
            )}

            {(isCancelled || isRefunded || !isDraft) && !(!isCancelled && !isRefunded && isDraft) && (
              <DropdownMenuItem disabled>
                <span className="opacity-50">
                  {isCancelled ? "Cancelled" : isRefunded ? "Refunded" : "Cannot Cancel"}
                </span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 